import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { MODULE_PAGE_CSS } from './moduleBannerStyles';

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const today = () => new Date().toISOString().split('T')[0];

const MAINT_FILTERS = ['All', 'Open', 'Urgent', 'In progress', 'Completed', 'From tenant'] as const;

const PRIORITY_META: Record<string, { bg: string; color: string; icon: string }> = {
  urgent:  { bg: '#fee2e2', color: '#dc2626', icon: '🔴' },
  routine: { bg: '#fef3c7', color: '#d97706', icon: '🟡' },
  low:     { bg: '#f0fdf4', color: '#16a34a', icon: '🟢' },
};
const STATUS_META: Record<string, { bg: string; color: string; label: string }> = {
  reported:    { bg: '#eff6ff', color: '#2563eb', label: 'Reported' },
  in_progress: { bg: '#fef3c7', color: '#d97706', label: 'In Progress' },
  'in progress':{ bg: '#fef3c7', color: '#d97706', label: 'In Progress' },
  completed:   { bg: '#f0fdf4', color: '#16a34a', label: 'Completed' },
};

const normStatus = (s: string) => {
  const x = (s || 'reported').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (x === 'open') return 'reported';
  if (x === 'closed') return 'completed';
  return x;
};
const normPriority = (p: string) => {
  const x = (p || 'routine').toLowerCase();
  if (x === 'high') return 'urgent';
  if (x === 'medium') return 'routine';
  return x;
};
const jobDescription = (j: any) => j.description || j.title || 'Untitled job';

/* ── CSS ─────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;600;700;800&family=DM+Mono:wght@400;500&display=swap');

.mx-wrap * { box-sizing: border-box; }
.mx-wrap { font-family: 'Bricolage Grotesque', system-ui, sans-serif; }

/* Alert banner */
.alert-banner { border-radius: 14px; padding: 14px 18px; margin-bottom: 14px; border: 1.5px solid; display: flex; align-items: flex-start; gap: 12px; }
.alert-icon  { font-size: 18px; flex-shrink: 0; margin-top: 2px; }
.alert-body  { flex: 1; min-width: 0; }
.alert-title { font-size: 13px; font-weight: 800; margin-bottom: 8px; }
.alert-item  { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 6px 0; border-top: 1px solid rgba(0,0,0,.06); flex-wrap: wrap; }
.alert-name  { font-size: 13px; font-weight: 700; color: #0f172a; }
.alert-meta  { font-size: 12px; color: #64748b; margin-top: 2px; }

/* Toolbar */
.toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-bottom: 20px; }
.search-box { display: flex; align-items: center; gap: 8px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 8px 14px; flex: 1; min-width: 180px; max-width: 260px; }
.search-box input { border: none; outline: none; font-size: 13px; font-family: 'Bricolage Grotesque', sans-serif; color: #0f172a; background: transparent; width: 100%; }
.search-box input::placeholder { color: #94a3b8; }
.filter-btn { padding: 8px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff; color: #64748b; font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'Bricolage Grotesque', sans-serif; transition: all .15s; white-space: nowrap; }
.filter-btn:hover  { border-color: #ea580c; color: #ea580c; }
.filter-btn.active { background: #0f172a; border-color: #0f172a; color: #fff; }
.filter-count { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; font-size: 10px; font-weight: 800; margin-left: 5px; }
.filter-btn.active .filter-count { background: rgba(255,255,255,.2); }
.mx-filter {
  padding: 10px 36px 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0;
  background: #fff; font-size: 13px; font-weight: 700; color: #012169;
  appearance: none; min-width: 180px; cursor: pointer; font-family: inherit;
}
.mx-alert-slim {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-radius: 12px; margin-bottom: 14px; font-size: 13px; font-weight: 700;
}
.mx-alert-slim.red { background: #fef2f2; border: 1.5px solid #fecaca; color: #991b1b; }
.mx-alert-slim.amber { background: #fffbeb; border: 1.5px solid #fde68a; color: #92400e; }
.portal-badge { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 6px; background: #dbeafe; color: #1d4ed8; margin-left: 6px; vertical-align: middle; }
.result-info { font-size: 12px; color: #94a3b8; font-weight: 600; margin-left: auto; }

/* Table */
.mx-table-wrap { border-radius: 14px; border: 1.5px solid #e9ecef; overflow: hidden; }
.mx-table { width: 100%; border-collapse: collapse; background: #fff; }
.mx-table th { padding: 11px 14px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; background: #f8fafc; border-bottom: 1.5px solid #e9ecef; white-space: nowrap; }
.mx-table td { padding: 13px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.mx-table tbody tr.mx-data-row:last-of-type td { border-bottom: none; }
.mx-table tbody tr.mx-data-row:hover td { background: #fafbfc; }
.mx-table tbody tr.mx-edit-row td { background: #fff7ed; border-bottom: 1.5px solid #ea580c; padding: 20px 16px; }

.job-desc { font-size: 14px; font-weight: 700; color: #0f172a; }
.job-ref  { font-size: 11px; color: #94a3b8; font-family: 'DM Mono', monospace; margin-top: 2px; }
.job-prop { font-size: 13px; color: #374151; font-weight: 600; }
.job-meta { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.job-date { font-size: 13px; color: #374151; font-family: 'DM Mono', monospace; }

/* Status quick-change */
.status-select { border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 5px 10px; font-size: 12px; font-weight: 700; font-family: 'Bricolage Grotesque', sans-serif; cursor: pointer; background: #fff; }

/* Badges */
.badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 7px; font-size: 12px; font-weight: 800; white-space: nowrap; }

/* Action buttons */
.btn-act { padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; border: 1.5px solid; transition: all .15s; font-family: 'Bricolage Grotesque', sans-serif; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px; text-decoration: none; }
.btn-act:hover { transform: translateY(-1px); filter: brightness(1.05); }
.btn-act:active { transform: translateY(0); }
.btn-act:disabled { opacity: .5; cursor: default; transform: none; }
.actions-cell { display: flex; gap: 6px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }

/* Toasts */
.toast-ok  { background: #f0fdf4; color: #16a34a; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
.toast-err { background: #fff1f1; color: #dc2626; border: 1.5px solid #fecaca; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }

/* Add/Edit panel */
.mx-panel { background: #fff; border: 1.5px solid #ea580c; border-radius: 16px; padding: 24px; margin-bottom: 24px; }
.mx-panel-title { font-size: 16px; font-weight: 800; color: #ea580c; margin: 0 0 18px; }
.mx-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; }
.mx-grid .span2 { grid-column: span 2; }
.mx-grid .span3 { grid-column: span 3; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 5px; }
.field-input { width: 100%; padding: 9px 12px; border: 1.5px solid #d1d5db; border-radius: 9px; font-size: 14px; font-family: 'Bricolage Grotesque', sans-serif; color: #0f172a; background: #fff; outline: none; transition: border-color .15s; }
.field-input:focus { border-color: #ea580c; }
.file-label { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; background: #fff7ed; border: 1.5px dashed #ea580c; border-radius: 9px; padding: 9px 16px; font-size: 13px; color: #ea580c; font-weight: 700; transition: background .15s; }
.file-label:hover { background: #ffedd5; }
.panel-actions { display: flex; gap: 10px; margin-top: 18px; }
.btn-primary { background: #ea580c; color: #fff; border: none; border-radius: 9px; padding: 10px 24px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: 'Bricolage Grotesque', sans-serif; transition: all .15s; }
.btn-primary:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
.btn-primary:disabled { opacity: .6; cursor: default; }
.btn-secondary { background: #fff; color: #374151; border: 1.5px solid #d1d5db; border-radius: 9px; padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer; font-family: 'Bricolage Grotesque', sans-serif; }
.btn-secondary:hover { border-color: #94a3b8; }

/* Empty state */
.mx-empty { text-align: center; padding: 64px 32px; background: #fff; border: 2px dashed #e2e8f0; border-radius: 18px; }
.mx-empty .empty-icon { font-size: 48px; margin-bottom: 14px; }
.mx-empty h3 { font-size: 18px; font-weight: 800; color: #374151; margin: 0 0 8px; }
.mx-empty p  { color: #94a3b8; font-size: 14px; margin: 0 0 20px; }

/* Responsive */
@media (max-width: 750px) {
  .mx-grid .span2, .mx-grid .span3 { grid-column: span 1; }
  .mx-table th:nth-child(3), .mx-table td:nth-child(3),
  .mx-table th:nth-child(6), .mx-table td:nth-child(6) { display: none; }
}
`;

const ACCESS_NOTICE_OPTIONS = [
  { value: '', label: '— Not set —' },
  { value: 'emergency', label: '🚨 Emergency — immediate access (safety / habitability)' },
  { value: 'standard_24h', label: '📋 Standard — 24 hours written notice (Section 11)' },
  { value: 'agreed', label: '🤝 Agreed — appointment with tenant' },
  { value: 'not_required', label: '⏸ Not required — access not yet needed' },
] as const;

const BLANK_FORM = {
  property_address: '', description: '', priority: 'routine', reported_date: today(), contractor: '',
  cost: '', notes: '', tenant_name: '', due_date: '', actual_cost: '', job_ref: '',
  access_notice_type: '', access_notice_date: '',
};

type FileEntry = { url: string; name: string; uploaded_at?: string };

const parseFileUrls = (j: any): FileEntry[] => {
  if (Array.isArray(j.file_urls) && j.file_urls.length) return j.file_urls;
  if (j.file_url) return [{ url: j.file_url, name: 'Attachment' }];
  return [];
};

export default function Maintenance() {
  const { token } = useAuth();
  const location = useLocation();
  const [jobs, setJobs]           = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [filterTab, setFilterTab]   = useState<string>('All');
  const [search, setSearch]         = useState('');
  const [showUrgent, setShowUrgent] = useState(false);
  const [showOverdue, setShowOverdue] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [form, setForm]           = useState({ ...BLANK_FORM });
  const [addFiles, setAddFiles]   = useState<File[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm]   = useState<any>({});
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedJobs, setArchivedJobs] = useState<any[]>([]);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const normaliseJob = (j: any) => ({
    ...j,
    description: jobDescription(j),
    status: normStatus(j.status),
    priority: normPriority(j.priority),
  });

  const load = useCallback(async () => {
    if (!token) return; setLoading(true);
    const [m, p] = await Promise.all([
      fetch(`${API_URL}/landlord/maintenance`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/landlord/properties`,  { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]);
    setJobs((m.maintenance || m.jobs || []).map(normaliseJob));
    setProperties(p.properties || []);
    setLoading(false);
  }, [token]);

  const loadArchived = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API_URL}/landlord/maintenance?archived=true`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    setArchivedJobs((d.maintenance || d.jobs || []).map(normaliseJob));
  }, [token]);

  useEffect(() => { load(); loadArchived(); }, [load, loadArchived]);

  useEffect(() => {
    const prefill = (location.state as { prefill?: Record<string, string> })?.prefill;
    if (!prefill) return;
    setShowAdd(true);
    setEditingId(null);
    setForm({ ...BLANK_FORM, ...prefill });
    setError('');
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const uploadFiles = async (jobId: number, files: File[]) => {
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    await fetch(`${API_URL}/landlord/maintenance/${jobId}/files`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
  };

  const save = async () => {
    if (!form.property_address || !form.description.trim()) { setError('Property and description are required'); return; }
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/maintenance`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...form,
        cost: Number(form.cost) || 0,
        actual_cost: Number(form.actual_cost) || null,
        access_notice_type: form.access_notice_type || null,
        access_notice_date: form.access_notice_date || null,
        status: 'reported',
      }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed to log job'); setSaving(false); return; }
    if (addFiles.length && d.job?.id) await uploadFiles(d.job.id, addFiles);
    setSuccess('Job logged'); setShowAdd(false); setForm({ ...BLANK_FORM }); setAddFiles([]);
    load(); setSaving(false); setTimeout(() => setSuccess(''), 3000);
  };

  const quickStatus = async (id: number, status: string) => {
    setUpdatingId(id);
    const body: any = { status };
    if (status === 'completed') body.completed_date = today();
    await fetch(`${API_URL}/landlord/maintenance/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    load(); setUpdatingId(null);
  };

  const startEdit = (j: any) => {
    setEditingId(j.id); setEditFiles([]); setShowAdd(false); setError('');
    setEditForm({
      description: jobDescription(j), priority: normPriority(j.priority), contractor: j.contractor || '',
      cost: j.cost || '', notes: j.notes || '', landlord_note: j.landlord_note || '', tenant_name: j.tenant_name || '',
      due_date: j.due_date?.split('T')[0] || '', actual_cost: j.actual_cost || '',
      job_ref: j.job_ref || '', completed_date: j.completed_date?.split('T')[0] || '',
      access_notice_type: j.access_notice_type || '', access_notice_date: j.access_notice_date?.split('T')[0] || '',
    });
  };

  const saveEdit = async (id: number) => {
    setSaving(true); setError('');
    const r = await fetch(`${API_URL}/landlord/maintenance/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...editForm,
        cost: editForm.cost || null,
        actual_cost: editForm.actual_cost || null,
        access_notice_type: editForm.access_notice_type || null,
        access_notice_date: editForm.access_notice_date || null,
      }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || 'Failed to update'); setSaving(false); return; }
    if (editFiles.length) await uploadFiles(id, editFiles);
    setSuccess('Job updated'); setEditingId(null); load(); setSaving(false); setTimeout(() => setSuccess(''), 3000);
  };

  const archiveJob = async (j: any) => {
    if (!window.confirm(`Archive "${jobDescription(j)}"?`)) return;
    setArchivingId(j.id);
    await fetch(`${API_URL}/landlord/maintenance/${j.id}/archive`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    load(); loadArchived(); setArchivingId(null); setEditingId(null);
    setSuccess('Job archived'); setTimeout(() => setSuccess(''), 3000);
  };

  const restoreJob = async (id: number) => {
    await fetch(`${API_URL}/landlord/maintenance/${id}/restore`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    load(); loadArchived();
    setSuccess('Job restored'); setTimeout(() => setSuccess(''), 3000);
  };

  /* ── Computed ─────────────────────────────────────────────────── */
  const counts = {
    all: jobs.length,
    open: jobs.filter(j => normStatus(j.status) !== 'completed').length,
    urgent: jobs.filter(j => normPriority(j.priority) === 'urgent' && normStatus(j.status) !== 'completed').length,
    in_progress: jobs.filter(j => normStatus(j.status) === 'in_progress').length,
    completed: jobs.filter(j => normStatus(j.status) === 'completed').length,
    from_tenant: jobs.filter(j => j.tenant_submitted && normStatus(j.status) !== 'completed').length,
  };
  const totalCost = jobs.reduce((s, j) => s + Number(j.actual_cost || j.cost || 0), 0);

  const sorted = [...jobs].sort((a, b) => {
    // urgent open first, then by reported_date desc
    const aUrgent = normPriority(a.priority) === 'urgent' && normStatus(a.status) !== 'completed' ? 0 : 1;
    const bUrgent = normPriority(b.priority) === 'urgent' && normStatus(b.status) !== 'completed' ? 0 : 1;
    if (aUrgent !== bUrgent) return aUrgent - bUrgent;
    return new Date(b.reported_date).getTime() - new Date(a.reported_date).getTime();
  });

  const displayed = sorted.filter(j => {
    const ns = normStatus(j.status);
    const np = normPriority(j.priority);
    if (filterTab === 'Open' && ns === 'completed') return false;
    if (filterTab === 'Urgent' && !(np === 'urgent' && ns !== 'completed')) return false;
    if (filterTab === 'In progress' && ns !== 'in_progress') return false;
    if (filterTab === 'Completed' && ns !== 'completed') return false;
    if (filterTab === 'From tenant' && !(j.tenant_submitted && ns !== 'completed')) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    const desc = jobDescription(j);
    return desc.toLowerCase().includes(q) || j.property_address.toLowerCase().includes(q)
      || (j.contractor || '').toLowerCase().includes(q) || (j.tenant_name || '').toLowerCase().includes(q)
      || (j.job_ref || '').toLowerCase().includes(q);
  });

  const filterCount = (f: string) => {
    if (f === 'Open') return counts.open;
    if (f === 'Urgent') return counts.urgent;
    if (f === 'In progress') return counts.in_progress;
    if (f === 'Completed') return counts.completed;
    if (f === 'From tenant') return counts.from_tenant;
    return 0;
  };

  const urgentOpen = jobs.filter(j => normPriority(j.priority) === 'urgent' && normStatus(j.status) !== 'completed');

  const todayStr = new Date().toISOString().split('T')[0];
  const overdueJobs = jobs.filter(j =>
    j.due_date &&
    j.due_date.split('T')[0] < todayStr &&
    normStatus(j.status) !== 'completed'
  );

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="mod-page mx-wrap">
      <style>{MODULE_PAGE_CSS}{CSS}</style>

      <div className="mod-banner-wrap mod-banner-maint">
        <div className="mod-banner-glass">
          <div>
            <h1>🛠️ Maintenance</h1>
            <p>{counts.open} open · {counts.from_tenant} from tenant portal · £{totalCost.toLocaleString()} spent — report, fix, close</p>
          </div>
          <div className="mod-banner-actions">
            <button type="button" className="mod-btn-white" style={{ marginRight: 8 }}
              onClick={() => { setShowArchived(a => !a); if (!showArchived) loadArchived(); }}>
              {showArchived ? '✖ Hide' : `📦 Archived${archivedJobs.length ? ` (${archivedJobs.length})` : ''}`}
            </button>
            <button type="button" className="mod-btn-white" onClick={() => { setShowAdd(true); setEditingId(null); setForm({ ...BLANK_FORM }); setAddFiles([]); setError(''); }}>🛠️ Job</button>
          </div>
        </div>
      </div>

      <div className="mod-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="mod-kpi mod-kpi-teal">
          <div className="kl">Open</div>
          <div className="kv">{counts.open}</div>
          <div className="ks">{counts.open > 0 ? 'needs action' : 'all clear'}</div>
        </div>
        <div className="mod-kpi mod-kpi-red">
          <div className="kl">Urgent</div>
          <div className="kv">{counts.urgent}</div>
          <div className="ks">{counts.urgent > 0 ? 'act now' : 'none'}</div>
        </div>
        <div className="mod-kpi mod-kpi-green">
          <div className="kl">Completed</div>
          <div className="kv">{counts.completed}</div>
          <div className="ks">resolved</div>
        </div>
        <div className="mod-kpi mod-kpi-blue">
          <div className="kl">Total cost</div>
          <div className="kv" style={{ fontSize: totalCost >= 10000 ? 22 : 30 }}>£{totalCost.toLocaleString()}</div>
          <div className="ks">posted on complete</div>
        </div>
      </div>

      {urgentOpen.length > 0 && !showUrgent && (
        <div className="mx-alert-slim red">
          <span>🚨 {urgentOpen.length} urgent job{urgentOpen.length > 1 ? 's' : ''} need attention</span>
          <button type="button" className="btn-act" style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
            onClick={() => { setShowUrgent(true); setFilterTab('Urgent'); }}>👁 View</button>
        </div>
      )}

      {overdueJobs.length > 0 && !showOverdue && (
        <div className="mx-alert-slim amber">
          <span>⏰ {overdueJobs.length} job{overdueJobs.length > 1 ? 's' : ''} past due date</span>
          <button type="button" className="btn-act" style={{ background: '#012169', color: '#fff', borderColor: '#012169' }}
            onClick={() => setShowOverdue(true)}>👁 View</button>
        </div>
      )}

      {showOverdue && overdueJobs.length > 0 && (
        <div className="alert-banner" style={{ background: '#fffbeb', borderColor: '#fde68a', marginBottom: 14 }}>
          <div className="alert-body" style={{ width: '100%' }}>
            <div className="alert-title" style={{ color: '#92400e', display: 'flex', justifyContent: 'space-between' }}>
              <span>Overdue jobs</span>
              <button type="button" className="btn-act" style={{ background: '#fff', color: '#64748b', borderColor: '#e2e8f0' }} onClick={() => setShowOverdue(false)}>✖ Hide</button>
            </div>
            {overdueJobs.map(j => (
              <div key={j.id} className="alert-item">
                <div>
                  <div className="alert-name">{jobDescription(j)}</div>
                  <div className="alert-meta">{j.property_address} · due {fmt(j.due_date)}</div>
                </div>
                <button className="btn-act" style={{ background: '#d97706', color: '#fff', borderColor: '#d97706' }}
                  onClick={() => quickStatus(j.id, 'in_progress')} disabled={updatingId === j.id}>
                  {updatingId === j.id ? '…' : '▶ Start'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error   && <div className="toast-err">❌ {error}</div>}
      {success && <div className="toast-ok">✅ {success}</div>}


      {showAdd && (
        <div className="mx-panel">
          <div className="mx-panel-title">🛠️ Log Job</div>
          <div className="mx-grid">
            <div>
              <label className="field-label">Property *</label>
              <select value={form.property_address} onChange={e => setForm(p => ({ ...p, property_address: e.target.value }))} className="field-input">
                <option value="">— Select property —</option>
                {properties.map((p: any) => <option key={p.id} value={p.address}>{p.address}, {p.postcode}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Priority</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="field-input">
                <option value="urgent">🔴 Urgent — safety or habitability</option>
                <option value="routine">🟡 Routine — general repair</option>
                <option value="low">🟢 Low — cosmetic or minor</option>
              </select>
            </div>
            <div className="span2">
              <label className="field-label">Description of Works *</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Boiler not heating — no hot water since Monday" rows={2} className="field-input" style={{ resize: 'vertical' }} />
            </div>
            <div>
              <label className="field-label">Tenant / Reporter</label>
              <input type="text" placeholder="Jane Smith" value={form.tenant_name} onChange={e => setForm(p => ({ ...p, tenant_name: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Job Reference</label>
              <input type="text" placeholder="MX-001" value={form.job_ref} onChange={e => setForm(p => ({ ...p, job_ref: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Date Reported</label>
              <input type="date" value={form.reported_date} onChange={e => setForm(p => ({ ...p, reported_date: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Contractor / Tradesperson</label>
              <input type="text" placeholder="J&R Plumbing" value={form.contractor} onChange={e => setForm(p => ({ ...p, contractor: e.target.value }))} className="field-input" />
            </div>
            <div>
              <label className="field-label">Estimated Cost (£)</label>
              <input type="number" placeholder="250" value={form.cost} onChange={e => setForm(p => ({ ...p, cost: e.target.value }))} className="field-input" />
            </div>
            <div className="span2">
              <label className="field-label">Notes / Updates</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional details, access instructions…" rows={2} className="field-input" style={{ resize: 'vertical' }} />
            </div>
            <div>
              <label className="field-label">Access Notice (UK legal)</label>
              <select value={form.access_notice_type} onChange={e => setForm(p => ({ ...p, access_notice_type: e.target.value }))} className="field-input">
                {ACCESS_NOTICE_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Notice / Access Date</label>
              <input type="date" value={form.access_notice_date} onChange={e => setForm(p => ({ ...p, access_notice_date: e.target.value }))} className="field-input" />
            </div>
            <div className="span2">
              <label className="field-label">Attach Photos or Documents (max 10MB each, up to 10 files)</label>
              <label className="file-label">
                📎 {addFiles.length ? `${addFiles.length} file${addFiles.length > 1 ? 's' : ''} selected` : 'Choose files…'}
                <input type="file" accept="image/*,.pdf" multiple style={{ display: 'none' }}
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    const valid = files.filter(f => f.size <= 10485760);
                    if (valid.length < files.length) setError('Each file must be under 10MB');
                    setAddFiles(valid.slice(0, 10));
                  }} />
              </label>
              {addFiles.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                  {addFiles.map(f => f.name).join(' · ')}
                </div>
              )}
            </div>
          </div>
          <div className="panel-actions">
            <button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : '💾 Save'}</button>
            <button onClick={() => { setShowAdd(false); setError(''); setAddFiles([]); }} className="btn-secondary">✖ Cancel</button>
          </div>
        </div>
      )}

      {!loading && jobs.length > 0 && (
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: '#94a3b8', fontSize: 14 }}>🔍</span>
            <input placeholder="Search job, property, contractor…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="mx-filter" value={filterTab} onChange={e => setFilterTab(e.target.value)}>
            {MAINT_FILTERS.map(f => (
              <option key={f} value={f}>{f}{f !== 'All' && filterCount(f) > 0 ? ` (${filterCount(f)})` : ''}</option>
            ))}
          </select>
          <span className="result-info">{displayed.length} shown</span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>Loading jobs…</div>
      ) : jobs.length === 0 ? (
        <div className="mx-empty">
          <div className="empty-icon">🔧</div>
          <h3>No maintenance jobs logged yet</h3>
          <p>Track repairs, contractor jobs and works orders.<br />Get a full cost history for your tax return.</p>
          <button className="btn-primary" onClick={() => { setShowAdd(true); setForm({ ...BLANK_FORM }); }}>🛠️ Job</button>
        </div>
      ) : displayed.length === 0 ? (
        <div className="mx-empty">
          <div className="empty-icon">🔍</div>
          <h3>No jobs match</h3>
          <p>Try a different filter or clear your search.</p>
          <button className="btn-secondary" onClick={() => { setFilterTab('All'); setSearch(''); }}>✖ Clear</button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div className="mx-table-wrap">
            <table className="mx-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Property</th>
                  <th>Tenant</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Due / Reported</th>
                  <th>Cost</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map(j => {
                  const ns = normStatus(j.status);
                  const np = normPriority(j.priority);
                  const pm = PRIORITY_META[np] || PRIORITY_META.routine;
                  const sm = STATUS_META[ns] || STATUS_META.reported;
                  const isEditing = editingId === j.id;
                  const costDisplay = j.actual_cost ? `£${Number(j.actual_cost).toLocaleString()} actual` : j.cost ? `£${Number(j.cost).toLocaleString()} est.` : '—';
                  const fileList = parseFileUrls(j);
                  const noticeLabel = ACCESS_NOTICE_OPTIONS.find(o => o.value === j.access_notice_type)?.label?.replace(/^[^\s]+\s/, '') || '';
                  return (
                    <React.Fragment key={j.id}>
                      <tr className="mx-data-row">
                        <td>
                          <div className="job-desc">
                            {jobDescription(j)}
                            {j.tenant_submitted && <span className="portal-badge">Portal</span>}
                          </div>
                          {j.job_ref && <div className="job-ref">#{j.job_ref}</div>}
                        </td>
                        <td>
                          <div className="job-prop">{j.property_address}</div>
                          {j.contractor && <div className="job-meta">🔨 {j.contractor}</div>}
                          {j.access_notice_type && j.access_notice_type !== 'not_required' && (
                            <div className="job-meta">📋 {noticeLabel}{j.access_notice_date ? ` · ${fmt(j.access_notice_date)}` : ''}</div>
                          )}
                        </td>
                        <td><div className="job-meta" style={{ color: '#374151' }}>{j.tenant_name || '—'}</div></td>
                        <td><span className="badge" style={{ background: pm.bg, color: pm.color }}>{pm.icon} {np.charAt(0).toUpperCase() + np.slice(1)}</span></td>
                        <td>
                          {ns === 'completed' ? (
                            <span className="badge" style={{ background: sm.bg, color: sm.color }}>✅ Completed</span>
                          ) : (
                            <select className="status-select" value={ns === 'in_progress' ? 'in_progress' : ns}
                              onChange={e => quickStatus(j.id, e.target.value)} disabled={updatingId === j.id}>
                              <option value="reported">Reported</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          )}
                        </td>
                        <td>
                          {j.due_date && <div className="job-date">Due {fmt(j.due_date)}</div>}
                          <div className="job-date" style={{ color: '#94a3b8', fontSize: 11 }}>Rep. {fmt(j.reported_date)}</div>
                        </td>
                        <td><div className="job-date" style={{ fontSize: 12 }}>{costDisplay}</div></td>
                        <td>
                          <div className="actions-cell">
                            {fileList.map((f, idx) => (
                              <a key={idx} href={f.url} target="_blank" rel="noopener noreferrer"
                                className="btn-act" style={{ background: '#fff7ed', color: '#ea580c', borderColor: '#ea580c' }}
                                title={f.name}>
                                📷 View
                              </a>
                            ))}
                            <button className="btn-act" style={{ background: '#f0f9ff', color: '#1d4ed8', borderColor: '#93c5fd' }}
                              onClick={() => startEdit(j)}>✏️ Edit</button>
                            {ns === 'completed' && (
                              <button className="btn-act" style={{ background: '#f8fafc', color: '#64748b', borderColor: '#cbd5e1' }}
                                onClick={() => archiveJob(j)} disabled={archivingId === j.id}>
                                {archivingId === j.id ? '…' : '📦 Archive'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Inline edit row */}
                      {isEditing && (
                        <tr className="mx-edit-row">
                          <td colSpan={8}>
                            <div style={{ fontWeight: 800, fontSize: 13, color: '#ea580c', marginBottom: 14 }}>✏️ Editing — {j.description}</div>
                            <div className="mx-grid">
                              <div className="span2">
                                <label className="field-label">Description *</label>
                                <textarea value={editForm.description} onChange={e => setEditForm((p: any) => ({ ...p, description: e.target.value }))} rows={2} className="field-input" style={{ resize: 'vertical' }} />
                              </div>
                              <div>
                                <label className="field-label">Priority</label>
                                <select value={editForm.priority} onChange={e => setEditForm((p: any) => ({ ...p, priority: e.target.value }))} className="field-input">
                                  <option value="urgent">🔴 Urgent</option>
                                  <option value="routine">🟡 Routine</option>
                                  <option value="low">🟢 Low</option>
                                </select>
                              </div>
                              <div>
                                <label className="field-label">Tenant / Reporter</label>
                                <input type="text" value={editForm.tenant_name} onChange={e => setEditForm((p: any) => ({ ...p, tenant_name: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Job Reference</label>
                                <input type="text" value={editForm.job_ref} onChange={e => setEditForm((p: any) => ({ ...p, job_ref: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Contractor</label>
                                <input type="text" value={editForm.contractor} onChange={e => setEditForm((p: any) => ({ ...p, contractor: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Due Date</label>
                                <input type="date" value={editForm.due_date} onChange={e => setEditForm((p: any) => ({ ...p, due_date: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Completed Date</label>
                                <input type="date" value={editForm.completed_date} onChange={e => setEditForm((p: any) => ({ ...p, completed_date: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Estimated Cost (£)</label>
                                <input type="number" value={editForm.cost} onChange={e => setEditForm((p: any) => ({ ...p, cost: e.target.value }))} className="field-input" />
                              </div>
                              <div>
                                <label className="field-label">Actual Cost (£)</label>
                                <input type="number" value={editForm.actual_cost} onChange={e => setEditForm((p: any) => ({ ...p, actual_cost: e.target.value }))} className="field-input" />
                              </div>
                              <div className="span2">
                                <label className="field-label">Note for tenant (visible in portal)</label>
                                <textarea value={editForm.landlord_note || ''} onChange={e => setEditForm((p: any) => ({ ...p, landlord_note: e.target.value }))} rows={2} className="field-input" style={{ resize: 'vertical' }} placeholder="e.g. Plumber booked for Thursday 10am" />
                              </div>
                              <div className="span2">
                                <label className="field-label">Internal notes</label>
                                <textarea value={editForm.notes} onChange={e => setEditForm((p: any) => ({ ...p, notes: e.target.value }))} rows={2} className="field-input" style={{ resize: 'vertical' }} />
                              </div>
                              <div>
                                <label className="field-label">Access Notice (UK legal)</label>
                                <select value={editForm.access_notice_type || ''} onChange={e => setEditForm((p: any) => ({ ...p, access_notice_type: e.target.value }))} className="field-input">
                                  {ACCESS_NOTICE_OPTIONS.map(o => <option key={o.value || 'none'} value={o.value}>{o.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="field-label">Notice / Access Date</label>
                                <input type="date" value={editForm.access_notice_date || ''} onChange={e => setEditForm((p: any) => ({ ...p, access_notice_date: e.target.value }))} className="field-input" />
                              </div>
                            </div>
                            {parseFileUrls(j).length > 0 && (
                              <div style={{ marginTop: 12 }}>
                                <label className="field-label">Existing attachments</label>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {parseFileUrls(j).map((f, idx) => (
                                    <a key={idx} href={f.url} target="_blank" rel="noopener noreferrer" className="btn-act"
                                      style={{ background: '#fff7ed', color: '#ea580c', borderColor: '#ea580c', fontSize: 11 }}>
                                      📷 View
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ marginTop: 12 }}>
                              <label className="field-label">Add Photos or Documents (optional, multiple)</label>
                              <label className="file-label" style={{ fontSize: 12 }}>
                                📎 {editFiles.length ? `${editFiles.length} new file${editFiles.length > 1 ? 's' : ''}` : 'Choose files…'}
                                <input type="file" accept="image/*,.pdf" multiple style={{ display: 'none' }}
                                  onChange={e => setEditFiles(Array.from(e.target.files || []).filter(f => f.size <= 10485760).slice(0, 10))} />
                              </label>
                            </div>
                            <div className="panel-actions">
                              <button onClick={() => saveEdit(j.id)} disabled={saving} className="btn-primary">{saving ? 'Saving…' : '💾 Save'}</button>
                              <button onClick={() => { setEditingId(null); setEditFiles([]); }} className="btn-secondary">✖ Cancel</button>
                              {normStatus(j.status) === 'completed' && (
                                <button onClick={() => archiveJob(j)} disabled={archivingId === j.id} className="btn-secondary" style={{ color: '#64748b' }}>
                                  {archivingId === j.id ? '…' : '📦 Archive'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showArchived && (
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
            Archived Jobs ({archivedJobs.length})
          </div>
          {archivedJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0' }}>
              No archived jobs — archive completed jobs from the list above
            </div>
          ) : (
            <div className="mx-table-wrap">
              <table className="mx-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Property</th>
                    <th>Completed</th>
                    <th>Cost</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedJobs.map(j => (
                    <tr key={j.id} className="mx-data-row">
                      <td>
                        <div className="job-desc" style={{ color: '#64748b' }}>{jobDescription(j)}</div>
                        {j.job_ref && <div className="job-ref">#{j.job_ref}</div>}
                      </td>
                      <td><div className="job-prop" style={{ color: '#64748b' }}>{j.property_address}</div></td>
                      <td><div className="job-date">{fmt(j.completed_date || j.reported_date)}</div></td>
                      <td><div className="job-date">{j.actual_cost ? `£${Number(j.actual_cost).toLocaleString()}` : j.cost ? `£${Number(j.cost).toLocaleString()} est.` : '—'}</div></td>
                      <td>
                        <div className="actions-cell">
                          {parseFileUrls(j).map((f, idx) => (
                            <a key={idx} href={f.url} target="_blank" rel="noopener noreferrer"
                              className="btn-act" style={{ background: '#f8fafc', color: '#64748b', borderColor: '#cbd5e1', fontSize: 11 }}>
                              📷 View
                            </a>
                          ))}
                          <button className="btn-act" style={{ background: '#eff6ff', color: '#2563eb', borderColor: '#93c5fd' }}
                            onClick={() => restoreJob(j.id)}>↩ Restore</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


